/* eslint-disable react/no-array-index-key */
import { Card, css, Heading, InfoIcon, Paragraph, styled } from 'canoe';
import { compose, connectWithHammock, ENDPOINTS, useActions } from 'hammock';
import React, { useCallback, useMemo, useState } from 'react';

import { ContentMetadata, ContentType, VisibilityState } from '@candu-ai/content/types';
import { ContentPlacement } from '@candu-ai/placement/types';
import { RetakeSnapshotNotification } from '@candu-ai/snapshots/components';
import { Snapshot } from '@candu-ai/snapshots/types';
import { Flags, useFlags } from 'commons/hooks';
import { isTooltipContent } from 'craft/constants';
import {
  cleanUpDisconnectedSnapshots,
  getDefaultPlacement,
  removeSnapshot,
} from 'craft/containers/Toolbox/tabs/Placements/utils';

import { Placement } from './Placement';
import { EmptyPlacementInstructions } from './components/EmptyPlacementInstructions';
import { ReadOnlyState } from './components/ReadOnlyState';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Subhead = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    margin-bottom: ${theme.spaces.s};
  `}
`;

const PlacementsContainer = styled(Card)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spaces.l};
  `}
`;

const Separator = styled.hr`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.GlitterGrayL10};
    margin-bottom: ${theme.spaces.l};
    margin-top: ${theme.spaces.l};
  `}
`;

const Link = styled.a`
  ${({ theme }) => css`
    color: ${theme.colors.BrandPurple};
  `}
`;

interface ContentPlacementsProps {
  contentId: string;
  contentType: ContentType;
  snapshots: Snapshot[];
  placements: ContentPlacement[];
  context?: {
    maxPlacements?: number;
    askForEdit?: boolean;
    canDelete?: boolean;
    canEdit?: boolean;
  };
  contentMetadata: ContentMetadata;
}

const ContentPlacementsImpl = ({
  contentId,
  placements,
  snapshots,
  contentType,
  context,
  contentMetadata,
}: ContentPlacementsProps) => {
  const canAddPlacement =
    typeof context?.maxPlacements === 'undefined' || placements.length < context?.maxPlacements;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingPlacement, setEditingPlacement] = useState<number | null>(null);
  const { [Flags.INLINE_SNAPSHOTS]: withInlineSnapshots } = useFlags();

  const { delete: deleteSnapshot } = useActions(ENDPOINTS.snapshots);
  const { update, create, delete: deletePlacement } = useActions(ENDPOINTS.contentPlacements);
  const { action: contentMetadataAction } = useActions(ENDPOINTS.contentMetadata);

  const onNewLocation = useCallback(
    async (placement: Record<string, unknown> & { id: string }) => {
      await create({
        body: placement,
        invalidates: {
          [ENDPOINTS.contentPlacements]: {
            query: { finder: 'byContentId', contentId },
          },
        },
      });
    },
    [contentId, create],
  );

  const onUpdate = async (placement: Record<string, unknown> & { id: string }) => {
    await update({
      id: placement.id,
      body: placement,
      invalidates: {
        [ENDPOINTS.contentPlacements]: {
          query: { finder: 'byContentId', contentId },
        },
      },
    }).then(() => {
      setEditingPlacement(null);
      setIsEditing(false);
    });
  };

  const deleteLocation = async (id: string) => {
    const placementToDelete = placements.find((p) => p.id === id);

    await deletePlacement({
      id,
      invalidates: {
        [ENDPOINTS.contentPlacements]: {
          query: { finder: 'byContentId', contentId },
        },
      },
    }).then(async () => {
      // remove snapshot that was tied to this placement unless it is Tooltip content
      if (!isTooltipContent(contentType) && placementToDelete?.snapshotId) {
        await removeSnapshot({
          deleteSnapshot,
          snapshotId: placementToDelete.snapshotId,
          contentId,
          hideNotification: true,
        });
      }

      // Snapshot cleanup!
      await cleanUpDisconnectedSnapshots({
        contentType,
        placements,
        snapshots,
        contentId,
        deleteSnapshot,
        excludeSnapshotId: placementToDelete?.snapshotId,
      });
    });
  };

  const deleteLocationAndUnpublish = async (id: string) => {
    await deleteLocation(id);
    await contentMetadataAction({
      name: 'toggleContentVisibility',
      query: {
        id: contentId,
        visibilityState: 'Off',
      },
      onSuccessNotification: {
        message: 'Content successfully unpublished',
      },
      invalidates: {
        [ENDPOINTS.contentMetadata]: { id: contentId },
        [ENDPOINTS.contentPublishStatus]: { id: contentId },
      },
    });
  };

  const defaultPlacement: ContentPlacement = useMemo(() => {
    return getDefaultPlacement(contentMetadata);
  }, [contentMetadata]);

  const withSnapshots = useMemo(
    () =>
      (withInlineSnapshots && contentType === ContentType.Inline) ||
      contentType === ContentType.Modal ||
      contentType === ContentType.HotspotGroup ||
      contentType === ContentType.Tour,
    [contentType, withInlineSnapshots],
  );

  return (
    <>
      {!placements.length &&
      withSnapshots &&
      contentMetadata.personalisationMode !== 'Experimentation' &&
      !snapshots.length ? (
        <EmptyPlacementInstructions contentId={contentId} showGrabSnapshot />
      ) : (
        <>
          <RetakeSnapshotNotification contentMetadata={contentMetadata} />

          <Heading size="XS" element="h3" strong color="OnyxGray">
            {isEditing ? 'Edit Placement' : 'Existing Placements'}
          </Heading>
          {!isEditing && (
            <Paragraph size="short" color="OnyxGray">
              Below is a list of where your Content has already been placed.
            </Paragraph>
          )}

          <Container>
            {placements.length < 1 ? (
              <EmptyPlacementInstructions contentId={contentId} />
            ) : (
              <PlacementsContainer elevation="+1">
                {editingPlacement !== null ? (
                  <Placement
                    exists
                    placement={placements[editingPlacement]}
                    onDelete={() => deleteLocation(placements[editingPlacement].id)}
                    onSave={onUpdate}
                    editing={editingPlacement !== null}
                    setEditing={() => {
                      setEditingPlacement(null);
                      setIsEditing(false);
                    }}
                  />
                ) : (
                  placements.map((item, index) => (
                    <div key={item.id}>
                      <ReadOnlyState
                        context={context}
                        placement={item}
                        snapshot={
                          placements.length === 1 && snapshots.length === 1
                            ? snapshots[0]
                            : undefined
                        }
                        isLast={placements.length === 1}
                        setEditable={() => {
                          setEditingPlacement(index);
                          setIsEditing(true);
                        }}
                        onDelete={() => deleteLocation(item.id)}
                        onDeleteAndUnpublish={() => deleteLocationAndUnpublish(item.id)}
                        isContentLive={contentMetadata.visibilityState === VisibilityState.Live}
                      />

                      {placements.length - 1 !== index && <Separator />}
                    </div>
                  ))
                )}
              </PlacementsContainer>
            )}
          </Container>
        </>
      )}

      {editingPlacement === null && canAddPlacement ? (
        <Container>
          <Heading element="h4" size="XS">
            Add a new placement
          </Heading>
          <Subhead>
            Placements are where your Content will appear.
            <InfoIcon />{' '}
            <Link
              href="https://docs.candu.ai/en/articles/5916841-embedding-candu-content"
              target="_blank"
            >
              Learn more about placing Content
            </Link>
          </Subhead>

          <PlacementsContainer elevation="+1">
            <Placement
              key={contentId}
              editing
              placement={defaultPlacement}
              onSave={onNewLocation}
            />
          </PlacementsContainer>
        </Container>
      ) : null}
    </>
  );
};

const withSnapshots = connectWithHammock(({ contentId }) => ({
  resourceName: 'snapshots',
  endpoint: ENDPOINTS.snapshots,
  query: { finder: 'byContentId', id: contentId },
}));

const withContentPlacements = connectWithHammock(({ contentId }) => ({
  endpoint: ENDPOINTS.contentPlacements,
  resourceName: 'placements',
  query: { finder: 'byContentId', contentId },
}));

const withContentMetadata = connectWithHammock(({ contentId }) => ({
  endpoint: ENDPOINTS.contentMetadata,
  id: contentId,
  resourceName: 'contentMetadata',
}));

export const ContentPlacements = compose(
  withSnapshots,
  withContentPlacements,
  withContentMetadata,
)(ContentPlacementsImpl);
